import { forwardRef, ReactElement, useState } from "react"
import { Box, ButtonBase, Stack, Typography, Slide, Dialog } from "@mui/material"
import { alpha, styled } from "@mui/material/styles"
import type { FC } from ".."
import { Link, SvgIcon, ResponsiveImage } from ".."
import StyledContainer from "./StyledContainer"
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"
import { TransitionProps } from "@mui/material/transitions"

const JudgesSection: FC = () => {
  const globals = filmFestivalGlobals.judgesSection
  return (
    <Box
      sx={(theme) => ({
        position: "relative",
        zIndex: 3,
        backgroundColor: theme.palette["tertiary.800"].main,
      })}
    >
      <StyledContainer maxWidth={1400}>
        <Box
          minHeight="100vh"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          py={14}
          gap={{ xs: 12, lg: 20 }}
          sx={(theme) => ({
            transition: "all 0.3s ease",
          })}
        >
          <Typography
            variant="body.sm"
            sx={(theme) => ({
              color: theme.palette["neutral.700"].main,
              fontWeight: 600,
              lineHeight: "120%!important",
              textAlign: "center",
              fontSize: "24px!important",
            })}
          >
            {globals.title}
          </Typography>
          <Stack gap={18} maxWidth={1094} width="100%">
            {globals.judges.map((judge) => (
              <JudgeCard key={judge.firstName} {...judge} />
            ))}
          </Stack>
        </Box>
      </StyledContainer>
    </Box>
  )
}

export default JudgesSection

type JudgeCardProps = {
  firstName: string
  lastName: string
  position: string
  company: string
  headshotUrl?: string
  imageUrl?: string
  description1?: string
  description2?: string
  socials?: { label: string; url: string }[]
}

// A sub-component for the judge card
const JudgeCard: FC<JudgeCardProps> = ({
  firstName,
  lastName,
  position,
  company,
  headshotUrl,
  imageUrl,
  description1,
  description2,
  socials,
}) => {
  const [open, setOpen] = useState(false)
  const image = {
    variants: [
      {
        width: 160,
        height: 160,
        url: headshotUrl,
        title: "",
        alt: "a couple traveling by longtail boat exploring epic limestone cliffs in a huge lake in Khao Sok National Park, Chiew Lan Lake, Thailand Surat Thani, men and woman in front of longtail boat",
        style: "default",
      },
    ],
  }
  return (
    <Stack
      role="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setOpen(true)
      }}
      display="flex"
      flexDirection="row"
      alignItems="center"
      marginLeft="auto"
      gap={{ xs: 4, lg: 6 }}
      justifyContent="center"
      sx={(theme) => ({
        "transition": "color 0.3s ease",
        "&:nth-child(2)": {
          flexDirection: "row-reverse",
          marginRight: "auto",
          marginLeft: 0,
        },
        "&:hover": {
          ".judge-name": {
            color: `${theme.palette["neutral.700"].main}!important`,
          },
          ".judge-icon": {
            backgroundColor: `${theme.palette["neutral.700"].main}!important`,
          },
        },
        [theme.breakpoints.down("lg")]: {
          "flexDirection": "column",
          "alignItems": "center",
          "marginLeft": "auto",
          "marginRight": "auto",
          "&:nth-child(2)": {
            flexDirection: "column",
            margin: 0,
          },
          ".judge-name": {
            fontSize: "56px!important",
            lineHeight: "50%!important",
          },
        },
      })}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={6}
        justifyContent="center"
        sx={(theme) => ({
          [theme.breakpoints.down("lg")]: {
            flexDirection: "column",
            alignItems: "center",
          },
        })}
      >
        <StyledTypography variant="heading.lg" className="judge-name" order={1}>
          {firstName}
        </StyledTypography>
        <Box position="relative" order={{ xs: 3, lg: 2 }}>
          <ResponsiveImage
            image={image}
            sx={(theme) => ({
              width: "160px",
              height: "160px",
              objectFit: "cover",
              borderRadius: theme.spacing(4),
            })}
          />
          <Box
            sx={(theme) => ({
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: theme.palette["primary.800"].main,
              mixBlendMode: "overlay",
              opacity: 0.2,
            })}
          />
          <Box
            className="judge-icon"
            sx={(theme) => ({
              position: "absolute",
              bottom: 5,
              right: 5,
              height: 28,
              width: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 999,
              backgroundColor: theme.palette["primary.800"].main,
              transition: "background-color 0.2s ease",
            })}
          >
            <SvgIcon
              viewBox={"0 0 12 12"}
              size={12}
              variant="plus"
              sx={{
                fill: (theme) => theme.palette["tertiary.800"].main,
              }}
            />
          </Box>
        </Box>
        <StyledTypography variant="heading.lg" className="judge-name" order={{ xs: 2, lg: 3 }}>
          {lastName}
        </StyledTypography>
      </Box>
      <Stack
        sx={(theme) => ({
          "> span": {
            color: theme.palette["neutral.700"].main,
            fontSize: "16px",
            lineHeight: "120%!important",
            textAlign: "left",
            fontWeight: 700,
            [theme.breakpoints.down("lg")]: {
              textAlign: "center",
            },
          },
        })}
      >
        <Typography variant="body.md">{position}</Typography>
        <Typography variant="body.md">{company}</Typography>
      </Stack>
      <JudgeInfoModal
        judge={{
          firstName,
          lastName,
          position,
          company,
          imageUrl,
          description1,
          description2,
          socials,
        }}
        open={open}
        handleClose={() => setOpen(false)}
      />
    </Stack>
  )
}

// A sub component for the judge name
const StyledTypography = styled(Typography)(
  ({ theme }) => `
    color: ${theme.palette["primary.800"].main};
    font-size: 72px!important;
    font-weight: 700;
    line-height: 90%!important;
    transition: color 0.2s ease;
  `
)

type JudgeInfoModalProps = {
  judge: JudgeCardProps
  open: boolean
  handleClose: () => void
}

// a sub component for the judge info modal
const JudgeInfoModal: FC<JudgeInfoModalProps> = ({ judge, open, handleClose }) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={(theme) => ({
        "& .MuiDialog-paper": {
          backgroundColor: theme.palette["tertiary.800"].main,
          borderRadius: { xs: theme.spacing(2), md: theme.spacing(6) },
          maxWidth: 1094,
        },
      })}
    >
      <Box position="relative">
        <JudgeInfoModalContent judge={judge} />
        <CloseButton handleClose={handleClose} />
      </Box>
    </Dialog>
  )
}

// A sub component for the judge info modal transition
export const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="up"
      ref={ref}
      {...props}
      timeout={{ enter: 600, exit: 300 }}
      easing={{
        enter: "cubic-bezier(1, 0, 0.5, 1)",
        exit: "cubic-bezier(1, 0, 0.5, 1)",
      }}
    />
  )
})

type JudgeInfoModalContentProps = {
  judge: JudgeCardProps
}

// A sub component for the judge info modal content
const JudgeInfoModalContent: FC<JudgeInfoModalContentProps> = ({ judge }) => {
  const { firstName, lastName, position, company, imageUrl, description1, description2, socials } =
    judge

  const image = {
    variants: [
      {
        width: 554,
        height: 771,
        url: imageUrl,
        title: "",
        alt: "test",
        style: "default",
      },
    ],
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
      gap={{ xs: 6, md: 15 }}
      padding={{ xs: 8, md: 0 }}
    >
      <Box
        position="relative"
        textAlign="center"
        maxWidth={{ xs: "280px", md: "554px" }}
        maxHeight={{ xs: "280px", md: "771px" }}
        margin="0 auto"
      >
        <ResponsiveImage
          image={image}
          sx={(theme) => ({
            maxWidth: { xs: "280px", md: "554px" },
            maxHeight: { xs: "280px", md: "771px" },
            aspectRatio: { xs: "1 / 1", md: "554 / 771" },
            width: "100%",
            objectFit: "cover",
            margin: "0 auto",
            borderRadius: { xs: theme.spacing(5), md: 0 },
          })}
        />
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#F05B2A",
            mixBlendMode: "overlay",
            opacity: 0.2,
            borderRadius: { xs: theme.spacing(5), md: 0 },
          })}
        />
      </Box>
      <Stack
        sx={(theme) => ({
          padding: { xs: 0, md: theme.spacing(15, 15, 10, 0) },
          gap: { xs: 3, md: 6 },
        })}
      >
        <Stack
          gap={3}
          flexDirection={{ xs: "column", md: "row" }}
          direction={{ xs: "row", md: "column" }}
          justifyContent={{ xs: "center", md: "flex-start" }}
          alignItems={{ xs: "center", md: "flex-start" }}
        >
          <StyledTypography
            variant="heading.lg"
            sx={{ fontSize: { xs: "44px!important", md: "72px!important" } }}
          >
            {firstName}
          </StyledTypography>
          <StyledTypography
            variant="heading.lg"
            sx={{
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: { xs: "44px!important", md: "72px!important" },
            }}
          >
            {lastName}
          </StyledTypography>
        </Stack>
        <Stack
          sx={(theme) => ({
            "> span": {
              color: theme.palette["neutral.700"].main,
              fontSize: "16px",
              lineHeight: "120%!important",
              textAlign: "left",
              fontWeight: 700,
              [theme.breakpoints.down("lg")]: {
                textAlign: "center",
              },
            },
          })}
        >
          <Typography variant="body.md">{position}</Typography>
          <Typography variant="body.md">{company}</Typography>
        </Stack>
        <Stack
          gap={4}
          mt={{ xs: 3, md: 6 }}
          sx={(theme) => ({
            "> span": {
              color: "#2A2A2A",
              fontSize: "16px",
              lineHeight: "150%!important",
              textAlign: "left",
              fontWeight: 400,
            },
          })}
        >
          <Typography variant="body.md">{description1}</Typography>
          <Typography variant="body.md">{description2}</Typography>
        </Stack>
        <Stack direction="row" gap={10} marginTop="auto" pt={5}>
          {socials?.map((social) => (
            <Box key={social.label} display="flex" alignItems="center" gap={2}>
              <Link
                href={social.url}
                target="_blank"
                sx={(theme) => ({
                  padding: 0,
                })}
              >
                <Typography variant="body.md" lineHeight="150%!important">
                  {social.label}
                </Typography>
              </Link>
              <SvgIcon
                variant="arrowOutward"
                size={8}
                sx={(theme) => ({
                  fill: theme.palette["neutral.700"].main,
                })}
              />
            </Box>
          ))}
        </Stack>
      </Stack>
    </Box>
  )
}

// A sub component for the close button
export const CloseButton: FC<{ handleClose: () => void }> = ({ handleClose }) => {
  return (
    <Box
      position="absolute"
      display="flex"
      top={{ xs: 10, md: 20 }}
      right={{ xs: 10, md: 20 }}
      padding={2}
    >
      <ButtonBase onClick={handleClose}>
        <SvgIcon variant="close" viewBox="0 0 24 24" size={12} />
      </ButtonBase>
    </Box>
  )
}
